/* eslint-disable @typescript-eslint/no-empty-function */
import { ResourceEventHelper, ResourceScheduleHelper } from '@dispatch/shared';
import {
  useBulkUpdate,
  useCompany,
  useGetResourcesPaginated,
  usePermissions,
} from '@dispatch/shared/hooks';
import {
  AttributeForm,
  BulkUpdateExclusionZonesForm,
  BulkUpdatePostBody,
  EditEventForm,
  EditScheduleForm,
  Event,
  LocationForm,
  MultiSelectOption,
  ResourceBulkForm,
  Schedule,
} from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';
import { BulkUpdateResource } from '@dispatch/web/features/resources';
import { useNotification } from '@dispatch/web/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import _, { isEmpty, isNull } from 'lodash';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

const bulkUpdateSchema = yup.object().shape({
  type: yup.string().required('Type is required'),
  ids: yup.lazy((value) => {
    return Array.isArray(value)
      ? yup.array().required('Required field').typeError('Required field') // typeError is necessary here, otherwise we get a bad-looking yup error
      : yup.string().required('Required field');
  }),
});

export function BulkUpdateResourcePage() {
  const { tenantId } = useCurrentUser();
  const { company: companyData } = useCompany();
  const { permissions } = usePermissions();
  const { notifySuccess, notifyError } = useNotification();
  const [filterValue, setFilterValue] = React.useState<any | null>(null);
  const resourceData = useGetResourcesPaginated({
    filters: filterValue,
    enabled: !isNull(filterValue),
  });

  const resourceCount = React.useMemo(() => {
    return resourceData?.data?.length;
  }, [resourceData?.data]);

  const navigate = useNavigate();

  const form = useForm<BulkUpdateExclusionZonesForm>({
    resolver: yupResolver(bulkUpdateSchema),
  });

  const onSuccess = ({ success }: { success: boolean; message?: string }) => {
    if (success) {
      notifySuccess(
        'Update Successful',
        'Bulk resources update was successful'
      );
      form.reset({}, { keepValues: false });
      navigate('/resources');
    } else {
      notifyError('Error', 'Unable to update Resources');
    }
  };

  const { upsertBulk: upsertReplace, upsertBulkStatus: upsertReplaceStatus } =
    useBulkUpdate(onSuccess);

  const companyOptions = React.useMemo(
    () => ({
      Team:
        companyData?.team?.map((team) => ({
          title: team.name,
          value: team.integrationGUID,
        })) || [],
      Office:
        companyData?.office?.map((office) => ({
          title: office.name,
          value: office.integrationGUID,
        })) || [],
      Role:
        companyData?.role?.map((role) => ({
          title: role.name,
          value: role.integrationGUID,
        })) || [],
      Group:
        companyData?.group?.map((group) => ({
          title: group.name,
          value: group.integrationGUID,
        })) || [],
    }),
    [companyData]
  );

  const removeEmptyValues = (obj: any): unknown => {
    if (
      obj === null ||
      obj === undefined ||
      obj === '' ||
      (Array.isArray(obj) && obj.length === 0)
    ) {
      return undefined;
    }

    if (typeof obj === 'object' && !Array.isArray(obj)) {
      const result: any = {};
      Object.keys(obj).forEach((key) => {
        const value = removeEmptyValues(obj[key]);
        if (value !== undefined) {
          result[key] = value;
        }
      });
      return Object.keys(result).length > 0 ? result : undefined;
    }

    return obj;
  };

  const allFieldsUndefined = (obj: unknown): boolean => {
    if (_.isNil(obj)) {
      return true;
    }

    if (_.isBoolean(obj)) {
      return false;
    }

    if (_.isNumber(obj)) {
      return false;
    }

    if (_.isObject(obj)) {
      return _.every(obj, (value) => allFieldsUndefined(value));
    }

    return _.isEmpty(obj);
  };

  const createEvents = (values: any) => {
    if (!values) {
      notifyError('Error', 'Unable to Save Event');
    }

    const eventsArray = values?.map((value: EditEventForm) => {
      const events = ResourceEventHelper(value, ' ', tenantId || '');
      return events;
    });

    return eventsArray;
  };

  const createSchedules = (values: EditScheduleForm[]): Schedule[] | null => {
    if (!values) {
      notifyError('Error', 'Unable to Save Schedule');
      return null;
    }

    const processedSchedules: Schedule[] = [];

    for (const value of values) {
      const scheduleValue = {
        ...value,
        startDateTime: new Date(value.startDateTime),
        endDateTime: new Date(value.endDateTime),
      };
      const result = ResourceScheduleHelper(
        scheduleValue,
        ' ',
        processedSchedules,
        tenantId || ''
      );

      if (result.hasDuplicate) {
        const dates = result?.schedules?.map((schedule) =>
          new Date(schedule.workingDayStartTime).toDateString()
        );

        if (dates.length > 4) {
          dates.splice(3);
          dates.push('...');
        }

        notifyError(
          'Duplicate Schedule',
          `One or more days already have schedules, please revise the following dates: ${dates}`,
          { autoHideDuration: 4000 }
        );
        return null;
      }

      processedSchedules.push(...(result.schedules ?? []));
    }

    return processedSchedules;
  };

  const onBulkSave = (values: ResourceBulkForm) => {
    if (!values?.type || isEmpty(values?.ids))
      return notifyError('Error', 'Type and Ids are required');
    const usedIds: string[] = Array.isArray(values?.ids)
      ? ((() => {
          const ids = values?.ids as unknown;
          return (ids as MultiSelectOption[]).map((id) => id.value ?? id);
        })() as string[])
      : [values?.ids];

    const eventsData = values.events ? createEvents(values?.events) : [];
    const scheduleData = values?.schedules
      ? createSchedules(values?.schedules)
      : [];
    if (scheduleData === null) return;
    const schedules = scheduleData;
    const events = eventsData?.flatMap((event: Event) =>
      !isEmpty(event) ? event : []
    );

    // we don not need ids, type, or addslectedAttributeGUID in the post body
    // also need to map locations so need to destructure
    const {
      ids,
      type,
      addSelectedAttributeGUID,
      claimOwner,
      locations,
      repeatFor,
      isAppendValues,
      ...rest
    } = values;

    const isAppendValuesAsBooleans = {
      isAppendAttributes: JSON.parse(
        values?.isAppendValues?.isAppendAttributes || 'false'
      ) as boolean,
      isAppendLocations: JSON.parse(
        values?.isAppendValues?.isAppendLocations || 'false'
      ) as boolean,
      isAppendSchedules: JSON.parse(
        values?.isAppendValues?.isAppendSchedules || 'false'
      ) as boolean,
      isAppendTeamsOfficesRoles: JSON.parse(
        values?.isAppendValues?.isAppendTeamsOfficesRoles || 'false'
      ) as boolean,
    };

    const post: BulkUpdatePostBody = {
      target: {
        type: values?.type?.toLowerCase(),
        ids: usedIds,
      },
      isAppendValues: isAppendValuesAsBooleans,
    };

    if (claimOwner && !allFieldsUndefined(claimOwner)) {
      post.claimOwnerCapacity = values?.claimOwner;
    }

    const parsedLocations = locations?.map((location) => {
      const exclusionZones = {
        ...location?.exclusionZonesInformation,
        counties: (
          location.exclusionZonesInformation?.counties as MultiSelectOption[]
        )?.map((county: MultiSelectOption) => String(county.value)),
      };

      const inclusionZones = {
        ...location?.inclusionZonesInformation,
        counties: (
          location.inclusionZonesInformation?.counties as MultiSelectOption[]
        )?.map((county: MultiSelectOption) => String(county.value)),
      };

      return {
        ...location.locationInformation,
        ...location.serviceInformation,
        exclusionZones,
        inclusionZones,
      };
    });

    const postBody = removeEmptyValues({
      ...post,
      ...rest,
    }) as BulkUpdatePostBody;

    if (!isEmpty(rest?.attributes)) {
      const parseAttributes = rest?.attributes.map(
        (attribute: AttributeForm) => {
          if (attribute?.selectedAttributeValuesVarchars) {
            attribute.selectedAttributeValuesVarchars =
              attribute.selectedAttributeValuesVarchars.map((value) => {
                value.displayName = value.title;
                value.value = value.displayName;
                value.integrationGUID = uuidv4();
                return value;
              });
          }

          // TODO: there is an issue with types that needs a bigger fix. This is a bandaid for now to
          // unblock bulk resources adding attributes.
          if (isNull(attribute.selectedAttributeValuesBools)) {
            (attribute as any).selectedAttributeValuesBools = [];
          }
          return attribute;
        }
      );

      postBody.attributes = parseAttributes;
    }

    if (!isEmpty(locations)) {
      postBody.locations = parsedLocations as LocationForm[];
    }

    if (!isEmpty()) {
      postBody.teamsOfficesRoles = undefined;
    }

    if (!isEmpty(events)) {
      postBody.events = events;
    }

    if (!isEmpty(schedules)) {
      postBody.schedules = schedules;
    }

    upsertReplace(postBody);
  };

  return (
    <BulkUpdateResource
      upsertBulkStatus={upsertReplaceStatus}
      permissions={permissions}
      onBulkSave={onBulkSave}
      companyOptions={companyOptions}
      company={companyData}
      setFilterValue={setFilterValue}
      resourceCount={resourceCount}
    />
  );
}
